<script setup lang="ts">
import { formatMoney } from "~/utils/formatters"
withDefaults(
  defineProps<{
    showAvailability: boolean
    activeCookieName: string
    showQuery: boolean
    hasResultsNumberUnderFilters: boolean
  }>(),
  {
    showAvailability: true,
    activeCookieName: "",
    showQuery: true,
    hasResultsNumberUnderFilters: false
  }
)

const algoliaReset = useAlgoliaReset()

const route = useRoute()
const { t } = useI18n()

const currentDropdown = ref("")

const closeDropdown = () => (currentDropdown.value = "")
const toggleDropdown = (value: string) => {
  if (currentDropdown.value && currentDropdown.value === value) {
    closeDropdown()
    return
  }
  currentDropdown.value = value
}

onMounted(() => {
  useEventListener(document, "keydown", (e) => {
    if (e.key === "Escape" || e.keyCode === 27) {
      closeDropdown()
    }
  })
})

const statefulEntity = useGAEntity("pageList")

const updateActiveList = (items) => {
  const filteredItems = items.filter(
    (item) =>
      item.attribute === "isDiscounted" ||
      item.attribute === "price.amount_float" ||
      item.attribute === "brand.name" ||
      item.attribute === "mainCategory.name" ||
      item.attribute === "inStock" ||
      item.attribute === "deductibleProduct"
  )

  const sortedItems = filteredItems.sort((a, b) => {
    const attributeOrder = {
      isDiscounted: 1,
      "price.amount_float": 2,
      "brand.name": 3,
      "mainCategory.name": 4,
      inStock: 5,
      deductibleProduct: 6
    }

    return attributeOrder[a.attribute] - attributeOrder[b.attribute]
  })

  if (statefulEntity?.value?.filters) {
    statefulEntity.value.filters = sortedItems.flatMap((item) =>
      item.refinements.map((refinement) => ({
        type: refinement.attribute,
        value: refinement.label
      }))
    )
  }

  return sortedItems
}

type AlgoliaFilterResult = {
  attribute: string
  type: string
  value: number | string
  label: string
  count: number
  operator: string
  exhaustive: boolean
}

const labelFormatter = (refinement: AlgoliaFilterResult) => {
  let label = ""

  if (refinement.label === "true") {
    label = t(`filters.pills.${refinement.attribute}`)
  } else {
    if (
      refinement.attribute === "price.amount_float" &&
      refinement.operator.charAt(0) === ">"
    ) {
      label = "Maggiore di " + formatMoney(refinement.value as number)
    } else if (
      refinement.attribute === "price.amount_float" &&
      refinement.operator.charAt(0) === "<"
    ) {
      label = "Minore di " + formatMoney(refinement.value as number)
    } else {
      label = refinement.label
    }
  }

  return label
}

const { nbHits, query, totalActiveFilters } = useSwiftSearchHelper()
const { scopedId } = useAlgoliaWidgetScope()
</script>

<template>
  <div class="desktop-filters-bar pig col-span-full hidden space-y-4 md:block">
    <AlgoliaDesktopResultsNumber
      v-if="!hasResultsNumberUnderFilters"
      :query="query"
      :nbHits="nbHits"
      :showQuery="showQuery"
    />

    <div
      class="
        desktop-filters-bar__controls
        border-black-10
        flex flex-wrap
        justify-between
        border-b
      "
    >
      <div class="flex items-center gap-x-6">
        <p class="text-black-80">
          {{ $t("filters.utils.desktopFiltersBar.filterBy") }}
        </p>
        <AlgoliaDropdownsPrice
          :opened="currentDropdown === 'price'"
          facet-name="price.amount_float"
          @onToggle="toggleDropdown('price')"
        />
        <AlgoliaDropdownsOptions
          v-if="route.meta.page !== 'promotions-brand'"
          :text="$t('filters.utils.common.brands')"
          :search-placeholder="$t('filters.utils.dropdowns.brands.searchBrand')"
          :opened="currentDropdown === 'brands'"
          name="brand.name"
          id-facet="brand.name"
          content-class="min-w-[18rem] p-4"
          facet-name="brand.name"
          @onToggle="toggleDropdown('brands')"
        />
        <AlgoliaDropdownsOptions
          content-class="min-w-[24.5rem] p-4"
          :text="$t('filters.utils.common.categories')"
          :search-placeholder="
            $t('filters.utils.dropdowns.categories.searchCategory')
          "
          :opened="currentDropdown === 'categories'"
          name="mainCategory.name"
          id-facet="mainCategory.name"
          facet-name="mainCategory.name"
          @onToggle="toggleDropdown('categories')"
        />
        <AlgoliaDropdownsAvailability
          v-if="showAvailability"
          @onToggle="toggleDropdown('availability')"
          :opened="currentDropdown === 'availability'"
        />
      </div>

      <div class="flex items-center gap-x-6">
        <p class="text-black-80">
          {{ $t("filters.utils.desktopFiltersBar.orderBy") }}
        </p>
        <AisSortBy>
          <template v-slot="{ items, currentRefinement, refine }">
            <FiltersUtilsDropdownsSorting
              :items="items"
              :current-active="currentRefinement"
              :sort-by="items.filter((i: any) => i.value == currentRefinement)[0].label"
              :opened="currentDropdown === 'sorting'"
              :active-cookie-name="activeCookieName"
              @onToggle="toggleDropdown('sorting')"
              @on-select-item="
                (e: any) => {
                  refine(e.value)
                  closeDropdown()
                }
              "
            />
          </template>
        </AisSortBy>
      </div>
    </div>
    <div v-show="totalActiveFilters > 0" class="flex items-center">
      <AisCurrentRefinements
        :transform-items="updateActiveList"
        :id="scopedId('all')"
      >
        <template v-slot="{ items }">
          <div
            class="
              desktop-filters-bar__applied-filters
              flex flex-wrap
              items-center
              gap-4
            "
          >
            <template v-for="item in items" :key="item.attribute">
              <ChipsMyChips
                v-for="refinement of item.refinements"
                class="bg-green-10 whitespace-nowrap rounded-[100px]"
                :key="refinement.value"
                :message="labelFormatter(refinement)"
                :can-hide="false"
                is-with-icon
                @onClose="
                  () => {
                    const total = items.reduce(
                      (acc, current) => acc + current.refinements.length,
                      0
                    )
                    if (total === 1) algoliaReset?.()
                    item.refine(refinement)
                  }
                "
              />
            </template>
          </div>
        </template>
      </AisCurrentRefinements>
      <AisClearRefinements :id="scopedId('all')">
        <template v-slot="{ canRefine, refine }">
          <div>
            <UtilsButton
              v-if="canRefine"
              theme="ghost-green"
              class="mr-5 [&>div]:py-0"
              :text="$t('filters.utils.desktopFiltersBar.deleteFilters')"
              @click="
                () => {
                  algoliaReset?.()
                  refine()
                }
              "
            />
          </div>
        </template>
      </AisClearRefinements>
    </div>

    <AlgoliaDesktopResultsNumber
      v-if="hasResultsNumberUnderFilters"
      :query="query"
      :nbHits="nbHits"
      :showQuery="showQuery"
    />
  </div>
</template>
